import type { ChatNotice } from '@lemontaigu/chat';

// Pure « Infos & annonces » logic (C7) — no React, no store, unit-tested in
// noticesLogic.test.ts (the logic.ts pattern).

export type NoticeState = 'active' | 'upcoming' | 'expired';

/** Where a non-archived notice sits relative to its validity window. */
export function noticeState(
  notice: Pick<ChatNotice, 'startsAt' | 'endsAt'>,
  nowIso: string,
): NoticeState {
  if (notice.startsAt && notice.startsAt > nowIso) return 'upcoming';
  if (notice.endsAt && notice.endsAt < nowIso) return 'expired';
  return 'active';
}

/**
 * A guest-facing notice saved while the translator was down (FR-only): the
 * banner shows French to everyone until « Retraduire » succeeds.
 */
export function needsTranslation(notice: ChatNotice): boolean {
  return notice.showBanner && notice.bodyEn === null;
}

const STATE_ORDER: Record<NoticeState, number> = { active: 0, upcoming: 1, expired: 2 };

export interface GroupedNotices {
  /** Operational notices: active, then upcoming, then expired; newest first within a state. */
  notices: Array<{ notice: ChatNotice; state: NoticeState }>;
  /** Learned facts (AI-only, no window) — the collapsed « Base apprise » section. */
  facts: ChatNotice[];
}

export function groupNotices(all: ChatNotice[], nowIso: string): GroupedNotices {
  const notices = all
    .filter((n) => n.kind === 'notice')
    .map((notice) => ({ notice, state: noticeState(notice, nowIso) }))
    .sort(
      (a, b) =>
        STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
        (a.notice.createdAt < b.notice.createdAt ? 1 : -1),
    );
  const facts = all
    .filter((n) => n.kind === 'fact')
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  return { notices, facts };
}

// ── Editor field conversions ─────────────────────────────────────────────────
// The editor uses <input type="datetime-local"> (naive local values — the
// reception browser runs on hotel time); the API/store carry ISO UTC.

/** '' → null; otherwise the local datetime as ISO UTC. Invalid input → null. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/** ISO UTC → datetime-local value in the browser's timezone ('' for null). */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
